(() => {
  'use strict';

  // The sole ambient movement is a steady 20px/second, with no entrance,
  // hover lift or accelerating loop. Browsing always takes priority.
  const visitKey = 'guild-faithful-product-strip-browsed';
  const instances = new Map();
  let visitStopped = false;
  try { visitStopped = sessionStorage.getItem(visitKey) === 'true'; } catch { /* Use this page's memory when storage is unavailable. */ }

  function initialise(section) {
    if (instances.has(section)) return;
    const viewport = section.querySelector('[data-guild-strip-viewport]');
    const track = section.querySelector('[data-guild-strip-track]');
    if (!viewport || !track) return;
    const sources = [...track.querySelectorAll('[data-guild-strip-source]')];
    if (!sources.length) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const removers = [];
    let copies = [];
    let loopWidth = 0;
    let phase = 0;
    let frame = 0;
    let lastTime = 0;
    let visible = !('IntersectionObserver' in window);
    let stopped = visitStopped || reduced.matches;
    let pointerDown = false;
    let normaliseTimer = 0;
    let destroyed = false;

    function listen(target, type, callback, options) {
      target.addEventListener(type, callback, options);
      removers.push(() => target.removeEventListener(type, callback, options));
    }
    function pause() {
      cancelAnimationFrame(frame);
      frame = 0;
      lastTime = 0;
      section.dataset.stripMotion = stopped ? 'stopped' : 'paused';
    }
    function makeCopy(source) {
      const copy = source.cloneNode(true);
      copy.removeAttribute('data-guild-strip-source');
      copy.setAttribute('data-guild-strip-copy', '');
      copy.setAttribute('aria-hidden', 'true');
      copy.querySelectorAll('script, [id]').forEach((node) => {
        if (node.tagName === 'SCRIPT') node.remove();
        else node.removeAttribute('id');
      });
      // The copies preserve mouse-native product links and the existing
      // commerce action hooks, but never add a second keyboard/SR catalogue.
      copy.querySelectorAll('a, button, input, select, textarea, summary, [tabindex]').forEach((node) => node.setAttribute('tabindex', '-1'));
      track.append(copy);
      copies.push(copy);
      return copy;
    }
    function clearCopies() {
      copies.forEach((copy) => copy.remove());
      copies = [];
    }
    function measure() {
      if (stopped || destroyed) return;
      pause();
      const oldPosition = loopWidth ? viewport.scrollLeft % loopWidth : viewport.scrollLeft;
      clearCopies();
      const first = sources[0];
      const last = sources[sources.length - 1];
      const style = getComputedStyle(track);
      const gap = parseFloat(style.columnGap) || 0;
      loopWidth = last.offsetLeft - first.offsetLeft + last.offsetWidth + gap;
      // A short range stays still, with no repeated single product or fake
      // assortment added to fill the page.
      // So does a strip with no layout (inside something hidden): it has no width to fill and copies would never widen
      // it. The resize observer measures again once it can be seen.
      if (sources.length < 2 || !viewport.clientWidth || loopWidth <= viewport.clientWidth) {
        loopWidth = 0;
        viewport.scrollLeft = 0;
        section.dataset.stripMotion = 'static';
        return;
      }
      // A full second sequence guarantees identical pixels on both sides of
      // the wrap. Extra source copies are only needed for unusually wide rails.
      sources.forEach(makeCopy);
      // Bounded, so a track that stops widening (broken or missing styles) can never freeze the page.
      for (let sets = 1; sets < 8 && track.scrollWidth < loopWidth + viewport.clientWidth + first.offsetLeft; sets += 1) {
        sources.forEach(makeCopy);
      }
      phase = oldPosition % loopWidth;
      viewport.scrollLeft = phase;
      resume();
    }
    function tick(time) {
      frame = 0;
      if (stopped || reduced.matches || document.hidden || !visible || !loopWidth || destroyed) return pause();
      if (lastTime) {
        const elapsed = Math.min(time - lastTime, 64);
        const speed = Math.max(1, Math.min(40, Number(section.dataset.stripSpeed) || 20));
        // Keep fractional travel independently: some browsers round each
        // scrollLeft write, which would otherwise swallow a subpixel frame.
        phase = (phase + elapsed * speed / 1000) % loopWidth;
        viewport.scrollLeft = phase;
      }
      lastTime = time;
      section.dataset.stripMotion = 'running';
      frame = requestAnimationFrame(tick);
    }
    function resume() {
      if (!stopped && !reduced.matches && visible && !document.hidden && loopWidth && !frame && !destroyed) {
        lastTime = 0;
        frame = requestAnimationFrame(tick);
      }
    }
    function settleManualList() {
      if (pointerDown || destroyed || !loopWidth) return;
      clearTimeout(normaliseTimer);
      const position = viewport.scrollLeft % loopWidth;
      const firstOffset = sources[0].offsetLeft;
      let leading = 0;
      sources.forEach((source, index) => { if (source.offsetLeft <= position) leading = index; });
      const movedWidth = sources[leading].offsetLeft - firstOffset;
      const offset = Math.max(0, position - movedWidth);
      const rotated = [...sources.slice(leading), ...sources.slice(0, leading)];
      // Reorder the real links to the current visible sequence so taking over
      // does not snap back to the first product or leave a blank trailing edge.
      clearCopies();
      rotated.forEach((source) => track.append(source));
      sources.splice(0, sources.length, ...rotated);
      // For a range barely wider than its viewport, keep only the inaccessible
      // visual tail needed to preserve the current frame at handover.
      let index = 0;
      while (track.scrollWidth + 1 < offset + viewport.clientWidth && index < sources.length) {
        makeCopy(sources[index]);
        index += 1;
      }
      viewport.scrollLeft = offset;
      loopWidth = 0;
    }
    function stopForVisit(event) {
      stopped = true;
      if (!visitStopped) {
        visitStopped = true;
        try { sessionStorage.setItem(visitKey, 'true'); } catch { /* The in-memory stop still holds. */ }
        instances.forEach((instance, node) => { if (node !== section) instance.stop(); });
      }
      pause();
      if (event?.type === 'pointerdown' || event?.type === 'touchstart') return;
      settleManualList();
    }
    function manualKeys(event) {
      stopForVisit(event);
      if (event.target !== viewport) return;
      const first = sources[0];
      const step = first.offsetWidth + (parseFloat(getComputedStyle(track).columnGap) || 0);
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        viewport.scrollLeft += event.key === 'ArrowRight' ? step : -step;
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        viewport.scrollLeft = event.key === 'Home' ? 0 : viewport.scrollWidth;
      }
    }

    listen(section, 'pointerenter', (event) => { if (event.pointerType === 'mouse' || event.pointerType === 'pen') stopForVisit(event); }, { passive: true });
    listen(section, 'pointerdown', (event) => { pointerDown = true; stopForVisit(event); }, { passive: true });
    listen(window, 'pointerup', () => {
      if (!pointerDown) return;
      pointerDown = false;
      // Let a native link click complete before moving its DOM node.
      normaliseTimer = setTimeout(settleManualList, 180);
    }, { passive: true });
    listen(window, 'pointercancel', () => { pointerDown = false; normaliseTimer = setTimeout(settleManualList, 120); }, { passive: true });
    listen(section, 'touchstart', stopForVisit, { passive: true });
    listen(section, 'wheel', stopForVisit, { passive: true });
    listen(section, 'focusin', stopForVisit);
    listen(section, 'keydown', manualKeys);
    listen(document, 'visibilitychange', () => { if (document.hidden) pause(); else resume(); });
    listen(reduced, 'change', () => {
      if (reduced.matches) {
        stopped = true;
        pause();
        settleManualList();
      }
    });
    // Native momentum scrolling owns the rail after touch handover.
    listen(viewport, 'scrollend', () => { if (stopped) settleManualList(); }, { passive: true });
    listen(viewport, 'scroll', () => {
      if (!stopped || !loopWidth || pointerDown) return;
      clearTimeout(normaliseTimer);
      normaliseTimer = setTimeout(settleManualList, 180);
    }, { passive: true });

    const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
      visible = entries.some((entry) => entry.isIntersecting);
      if (visible) resume(); else pause();
    }, { threshold: 0.01 }) : null;
    observer?.observe(viewport);
    const resize = 'ResizeObserver' in window ? new ResizeObserver(measure) : null;
    resize?.observe(viewport);
    if (!resize) listen(window, 'resize', measure, { passive: true });

    instances.set(section, {
      stop: stopForVisit,
      destroy() {
        destroyed = true;
        pause();
        clearTimeout(normaliseTimer);
        observer?.disconnect();
        resize?.disconnect();
        removers.forEach((remove) => remove());
        clearCopies();
        instances.delete(section);
      }
    });
    if (stopped) section.dataset.stripMotion = 'stopped';
    else measure();
  }

  function scan(root = document) {
    if (root.matches?.('[data-guild-faithful-strip]')) initialise(root);
    root.querySelectorAll('[data-guild-faithful-strip]').forEach(initialise);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  else scan();
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((instance, node) => { if (event.target === node || event.target.contains(node)) instance.destroy(); });
  });
})();
